/**
 * Project history, pulled from LinkedIn (linkedin.com/in/rleroy/details/projects).
 * Entries with an `image` render that thumbnail in the timeline; the rest render
 * with no thumbnail &mdash; add an `image` field here to give one a photo.
 *
 * `caseStudy` is optional: an ordered array of content blocks (`paragraph`, `heading`,
 * or `list`) rendered when the entry is expanded. Entries without it show only the
 * short `description`.
 */

import Agent4Image from "assets/images/agent4.jpeg";
import Neo4JImage from "assets/images/neo4j.jpeg";
import MappingImage from "assets/images/mapping.jpeg";

const projectsData = [
  {
    title: "AI Data Enrichment",
    dateRange: "Sep 2025 – Jan 2026",
    description:
      "Uses the Perplexity API to enrich incoming inventory data. Unknown items are queued to Azure Functions for AI enrichment via structured prompts, giving users the details they'd otherwise have to search for manually and accelerating the mapping process.",
    caseStudy: [
      {
        type: "paragraph",
        text: "Some of the most elegant solutions are often simple. This is a great example of using simple AI to support co-work. Getting the pieces to work together was easy — tuning the prompts to maximize results took a bit longer.",
      },
      { type: "heading", text: "Business Problem" },
      {
        type: "paragraph",
        text: "The client receives monthly data from dozens of providers. The source files only have minimal data about inventory items. Users manually normalize and map data to a defined taxonomy, and do a lot of web searching to find more information about inventory items to aid the process.",
      },
      { type: "heading", text: "Solution" },
      {
        type: "paragraph",
        text: "Based on an idea from Tony Gordon at Snowflake, we updated our ingestion process with the Perplexity API. As data is received, we split the ingestion process into two paths:",
      },
      {
        type: "list",
        items: [
          "Known items go directly to the Data Lake without further processing.",
          "Unknown items are stored in the Data Lake. They are also queued to an Azure Function for further enrichment by AI.",
        ],
      },
      {
        type: "paragraph",
        text: "The Azure Function calls the Perplexity API. We structured the system prompt with specific internet search guidelines, and configure the user prompt with details about the new item number. The enriched data is amended to the new item in the Data Lake, and we updated the mapping UI to display the enriched data on demand.",
      },
      { type: "heading", text: "Business Benefit" },
      {
        type: "paragraph",
        text: "This newly enriched data expedites the data mapping process significantly. Rather than manually searching the internet, the enriched data has the details users seek. Users can quickly review the enriched data and make decisions.",
      },
    ],
  },
  {
    title: "Azure AI / Snowflake Cortex Agent",
    dateRange: "Jul 2025 – Sep 2025",
    description:
      "Augmented Metabase analytics with an AI agent that determines query intent. Unstructured data is handled through an NLP-to-SQL interface using GPT-4o for Snowflake SQL generation, with unstructured content stored in Azure AI Foundry blob storage and indexed for full-text search.",
    image: Agent4Image,
    caseStudy: [
      {
        type: "list",
        items: [
          "Using our normalized data, we augmented our Metabase analytics reporting tools with an AI agent that determines query intent (structured or unstructured) and responds accordingly.",
          "For unstructured data, we created prompts to drive an NLP-to-SQL interface using OpenAI GPT-4o to produce Snowflake SQL. That's used to query Snowflake and return results to our agents for output formatting.",
          "Our unstructured data is stored in AI Foundry blob storage and vector-indexed for full-text search using GPT-3.5 Turbo.",
          "The agent is written in React using a Python FastAPI middleware component.",
          "We're currently exploring ways to better visualize the results and extending the agent to incorporate Snowflake's Cortex technologies.",
        ],
      },
    ],
  },
  {
    title: "Neo4J Graph Database",
    dateRange: "Jul 2024 – Apr 2025",
    description:
      "Built a graph database environment to identify network connections between customers, using PageRank, similarity detection, and community detection through Cypher queries and Graph Data Science — integrated into a React application for customer scoring.",
    image: Neo4JImage,
    caseStudy: [
      {
        type: "list",
        items: [
          "My client had deep knowledge about their customers but little visibility into how those customers were linked together.",
          "I built a Neo4J graph database environment to identify network connections between customers, using PageRank, similarity detection, and community detection through Cypher queries and Graph Data Science.",
          "The graph database was integrated into an existing React application to score customers and identify next-best actions.",
        ],
      },
    ],
  },
  {
    title: "Data Mapping Application",
    dateRange: "Nov 2021 – Dec 2024",
    description:
      "A new web application for complex data mapping, built with a React UI, Python/Flask middleware, and a SQL Server database spanning hundreds of millions of rows. Stood up the entire Azure environment and ran the project through Trello sprints, cutting business processing times by 50%.",
    image: MappingImage,
    caseStudy: [
      {
        type: "list",
        items: [
          "Built an entirely new web application to support complex data mapping, using a React UI, Python/Flask middleware, and a Microsoft SQL database with hundreds of millions of rows. Used GitHub Actions for CI/CD.",
          "Created the entire Azure environment for this application, from networking and security to VMs, Application Services, and Azure Functions.",
          "Using Trello, we organized the backlog into sprints and worked with the client to prioritize the work, measure velocity, and ensure final delivery on time and under budget.",
          "Reduced business processing times by 50%.",
        ],
      },
    ],
  },
  {
    title: "Dynamics 365",
    dateRange: "Sep 2024 – Nov 2024",
    description:
      "Implemented Dynamics 365 Sales, migrated customer, product, and pricing data, then configured the system to match the client's specific needs and processes.",
    caseStudy: [
      {
        type: "list",
        items: [
          "Implemented Dynamics 365 Sales.",
          "Migrated all customers, products, and pricing.",
          "Configured the system for the client's specific needs and processes.",
        ],
      },
    ],
  },
  {
    title: ".Net API",
    dateRange: "Jun 2024 – Nov 2024",
    description:
      "Built a series of C# .NET APIs communicating with SQL Server, encapsulating business rules for consistent integration across multiple internal applications for a non-profit client.",
    caseStudy: [
      {
        type: "list",
        items: [
          "A large non-profit wanted to integrate data between multiple internal systems.",
          "Using Microsoft C# .NET, I created a series of APIs that talk to Microsoft SQL Server and encapsulate the business rules that allow consistent integration across applications.",
        ],
      },
    ],
  },
  {
    title: "Business Central and Shopify",
    dateRange: "Jan 2024",
    description:
      "Customized AL programming flows to automate the retail returns process between Shopify and the Dynamics Business Central accounting system.",
    caseStudy: [
      {
        type: "list",
        items: [
          "A retail client also sells through Shopify. Their accounting system is Microsoft Dynamics Business Central, and the returns process from Shopify didn't let them complete the full return-to-stock flow.",
          "Using the AL programming language, we customized their flows to automate the return process.",
        ],
      },
    ],
  },
  {
    title: "Vite Migration",
    dateRange: "Nov 2023 – Jan 2024",
    description:
      "Converted a React micro front-end's build process from Webpack to Vite, updated Node packages, enabled fast Hot Module Replacement, and documented the process for every micro app in the suite.",
    caseStudy: [
      {
        type: "list",
        items: [
          "My client had a React-based micro front-end application architecture, with a build process using Webpack. I converted it to Vite for build and run-time performance.",
          "Updated all Node packages to current versions.",
          "Changed the build process to support fast Hot Module Replacement (HMR) and use Rollup variables over React variables.",
          "Once the main application was completed, I documented the process and supported the migration for all the micro applications.",
        ],
      },
    ],
  },
  {
    title: "Market Research Analytics",
    dateRange: "Jan 2023 – Nov 2023",
    description:
      "Implemented a forked Metabase analytics instance on SQL Server for customer reporting, then migrated it to Snowflake using Snowpark — cutting monthly processing from hours to minutes.",
    caseStudy: [
      {
        type: "list",
        items: [
          "Implemented a forked version of Metabase Analytics on SQL Server as a customer-facing data reporting tool.",
          "Migrated from SQL Server to Snowflake using Snowpark to improve customer experience.",
          "New data pipelines reduced monthly processing from hours to minutes.",
        ],
      },
    ],
  },
  {
    title: "Wholesale Clothier Enterprise",
    dateRange: "Jan 2023 – Nov 2023",
    description:
      "Deployed a Microsoft Dynamics 365 Business Central migration from QuickBooks, handling implementation, configuration, process improvement, data migration, AL customizations, and user training.",
    caseStudy: [
      {
        type: "list",
        items: [
          "Deployed Microsoft Dynamics 365 Business Central as a migration from QuickBooks.",
          "Facilitated the system implementation, configuration, process improvement, data migration, and user training.",
          "Made several customizations to the system using AL to meet the client's specific needs.",
        ],
      },
    ],
  },
  {
    title: "WebCrawler",
    dateRange: "May 2023 – Sep 2023",
    description:
      "Built a Python web crawler with the Scrapy framework to traverse target sites for specific product IDs, enriching RAG models and improving the accuracy of the automated mapping process.",
    caseStudy: [
      {
        type: "list",
        items: [
          "To support the data mapping project, we didn't have enough data to match with accuracy — we needed more.",
          "Created a Python web crawler application, based on the Scrapy framework, to traverse a defined list of websites for specific product IDs.",
          "This data enriched the RAG models and greatly improved our ability to automate the mapping process.",
        ],
      },
    ],
  },
  {
    title: "Kitchen Appliance Distributor Integration",
    dateRange: "Apr 2023 – Sep 2023",
    description:
      "Built a C# .NET solution on Azure to process shipment data from a supplier into the accounting system — exchanged over FTP, parsed into a NoSQL database, and integrated via RESTful APIs and JSON.",
    caseStudy: [
      {
        type: "list",
        items: [
          "The client needed a way to bring shipment data from a third-party supplier into their accounting system. I built a solution that completely removes the need to manually enter any data.",
          "Custom-built an integration solution using Microsoft C# .NET running on Azure, accessing Azure Storage Accounts and SQL Server.",
          "Data is exchanged using FTP and parsed into a NoSQL database, then integrated into Prophet 21 using RESTful APIs and JSON.",
        ],
      },
    ],
  },
  {
    title: "Market Research AI/ML using RAG",
    dateRange: "Mar 2023 – May 2023",
    description:
      "Created a Qdrant vector database computing low-dimensional representations of inventory item descriptions, automating mapping by searching for matches and reporting potential mappings with confidence levels for user approval.",
    caseStudy: [
      {
        type: "list",
        items: [
          "The client receives sales data from more than 20 sources, then normalizes and maps the data for report comparisons across sources.",
          "Using the mapped data (item numbers, descriptions, and quantities), we created a Qdrant vector database to compute low-dimensional representations of inventory item descriptions for search and retrieval.",
          "As new data is received, we automate the mapping process by searching the vector database for matches and reporting potential mappings, with confidence levels, for user approval. This vector database powers search and retrieval through efficient, robust approximate-nearest-neighbor graphs.",
          "This model reduced mapping time and increased accuracy.",
          "By default, we use all-MiniLM-L12-v2 based on recent benchmarks. A bigger model like T5 could be used at the expense of slower indexing and real-time inference.",
        ],
      },
    ],
  },
  {
    title: "ETL Ingestion",
    dateRange: "Jan 2023 – Apr 2023",
    description:
      "Developed a pipeline handling monthly data feeds from dozens of providers, using Python and Pandas to build reusable classes that ingest Excel, text, and CSV files into Microsoft SQL Server.",
    caseStudy: [
      {
        type: "list",
        items: [
          "Custom-developed a pipeline to handle monthly data feeds from dozens of providers, normalizing the data for internal processing.",
          "Using Python and Pandas, created a series of reusable classes to ingest Excel, text, and CSV files into Microsoft SQL Server.",
        ],
      },
    ],
  },
  {
    title: "Custom SSIS",
    dateRange: "Jul 2020 – Sep 2020",
    description:
      "Used SSIS to receive daily shipment data from a supplier, transform it into a usable format, match it to SQL Server tables, and apply the changes — running for four years and eliminating thousands of manual work hours.",
    caseStudy: [
      {
        type: "list",
        items: [
          "There was a need to integrate daily shipment data from a key supplier.",
          "Used SSIS to receive the data, transform it into a usable format, match it to Microsoft SQL Server database tables, and apply the changes.",
          "This job has been running for four years now, eliminating thousands of hours of manual work.",
        ],
      },
    ],
  },
];

export default projectsData;
